import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import educationOpsService, {
  EducationWorkflowRecord,
  getEducationRecordId,
} from '../../services/educationOps.service';

const currentUser = (user: any) => (user?.user ? user.user : user);

const statusClass = (status: string) => {
  const value = String(status || '').toUpperCase();
  if (['ACCEPTED', 'ATTENDANCE_MARKED', 'COMPLETED', 'FEEDBACK_SUBMITTED', 'CONVERTED'].includes(value)) {
    return 'border-emerald-200 bg-emerald-50 text-emerald-800';
  }
  if (['REJECTED', 'CANCELLED'].includes(value)) {
    return 'border-rose-200 bg-rose-50 text-rose-800';
  }
  if (['REQUESTED', 'RESCHEDULED'].includes(value)) {
    return 'border-amber-200 bg-amber-50 text-amber-800';
  }
  return 'border-slate-200 bg-slate-50 text-slate-700';
};

const formatDate = (value?: string) => {
  if (!value) return 'Slot not set';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Slot not set' : date.toLocaleString();
};

const describeDemo = (record: EducationWorkflowRecord) => {
  const payload = record.payload || {};
  const subject = payload.subject || 'Subject';
  const mode = payload.classMode || payload.mode || 'Mode';
  return `${subject} | ${mode}`;
};

const DemoClassPanel: React.FC<{ compact?: boolean }> = ({ compact = false }) => {
  const { user } = useAuth();
  const resolvedUser = currentUser(user);
  const role = String(resolvedUser?.role || '').toUpperCase();
  const [records, setRecords] = useState<EducationWorkflowRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [feedbackById, setFeedbackById] = useState<Record<string, string>>({});
  const [rescheduleById, setRescheduleById] = useState<Record<string, string>>({});
  const [attendanceById, setAttendanceById] = useState<Record<string, { studentAttended: string; teacherAttended: string; notes: string }>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const nextRecords = await educationOpsService.listRecords({ kind: 'DEMO_CLASS' });
      setRecords(nextRecords);
    } catch (error) {
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const updateDemo = async (
    record: EducationWorkflowRecord,
    status: string,
    payload: Record<string, any> = {},
    scheduledAt?: string,
  ) => {
    const id = getEducationRecordId(record);
    if (!id) return;
    setMessage('');
    try {
      await educationOpsService.updateDemoStatus(id, status, payload, scheduledAt);
      setMessage('Demo updated.');
      await load();
    } catch (error: any) {
      setMessage(error?.response?.data?.message || 'Could not update demo.');
    }
  };

  const submitFeedback = async (record: EducationWorkflowRecord) => {
    const id = getEducationRecordId(record);
    const feedback = feedbackById[id]?.trim();
    if (!id || !feedback) return;
    await educationOpsService.submitDemoFeedback(id, { feedbackText: feedback });
    setFeedbackById((prev) => ({ ...prev, [id]: '' }));
    setMessage('Feedback submitted.');
    await load();
  };

  const recordAttendance = async (record: EducationWorkflowRecord) => {
    const id = getEducationRecordId(record);
    const attendance = attendanceById[id] || { studentAttended: 'YES', teacherAttended: 'YES', notes: '' };
    if (!id) return;
    setMessage('');
    try {
      await educationOpsService.recordDemoAttendance(id, attendance);
      setAttendanceById((prev) => ({
        ...prev,
        [id]: { studentAttended: 'YES', teacherAttended: 'YES', notes: '' },
      }));
      setMessage('Attendance recorded.');
      await load();
    } catch (error: any) {
      setMessage(error?.response?.data?.message || 'Could not record attendance.');
    }
  };

  const convertDemo = async (record: EducationWorkflowRecord) => {
    const id = getEducationRecordId(record);
    if (!id) return;
    await educationOpsService.convertDemoToPaid(id, {
      sourceStatus: record.status,
      teacherUserId: record.teacherUserId,
      studentUserId: record.studentUserId,
    });
    setMessage('Demo converted to paid tutor selection.');
    await load();
  };

  const rescheduleDemo = async (record: EducationWorkflowRecord) => {
    const id = getEducationRecordId(record);
    const nextSlot = rescheduleById[id];
    if (!id || !nextSlot) return;
    await updateDemo(
      record,
      'RESCHEDULED',
      {
        reschedule: {
          requestedByRole: role,
          newSlot: nextSlot,
          updatedAt: new Date().toISOString(),
        },
      },
      nextSlot,
    );
    setRescheduleById((prev) => ({ ...prev, [id]: '' }));
  };

  return (
    <section className="overflow-hidden rounded-[28px] border border-white/70 bg-white/90 shadow-[0_24px_70px_rgba(15,23,42,0.08)] backdrop-blur">
      <div className="border-b border-slate-200 px-5 py-5 sm:px-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Demo Classes</p>
            <h3 className="mt-2 text-2xl font-semibold tracking-tight text-slate-900">
              Demo requests and follow-ups
            </h3>
            <p className="mt-1 text-sm text-slate-500">
              {role === 'TEACHER'
                ? 'Accept, reject, reschedule, or complete student demo requests.'
                : 'Track requested demos, submit feedback, and continue with a tutor after completion.'}
            </p>
          </div>
          {role === 'STUDENT' && (
            <Link
              to="/student/find-teachers"
              className="inline-flex items-center justify-center rounded-2xl bg-slate-950 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700"
            >
              Find Tutors
            </Link>
          )}
        </div>
      </div>

      <div className={compact ? 'px-5 py-4 sm:px-6' : 'px-5 py-5 sm:px-6'}>
        {message && <p className="mb-4 rounded-2xl bg-slate-100 px-4 py-3 text-sm text-slate-700">{message}</p>}
        {loading ? (
          <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-sm text-slate-500">
            Loading demo classes...
          </div>
        ) : records.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-sm text-slate-500">
            No demo classes yet.
          </div>
        ) : (
          <div className="space-y-4">
            {records.map((record) => {
              const id = getEducationRecordId(record);
              const payload = record.payload || {};
              const canTeacherAct = role === 'TEACHER' || role === 'ADMIN';
              const canStudentAct = role === 'STUDENT' || role === 'ADMIN';
              return (
                <article key={id || record.title} className="rounded-[22px] border border-slate-200 bg-slate-50 px-4 py-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <h4 className="font-semibold text-slate-950">{record.title || 'Demo class'}</h4>
                        <span className={`rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] ${statusClass(record.status)}`}>
                          {record.status}
                        </span>
                      </div>
                      <p className="mt-2 text-sm text-slate-600">{describeDemo(record)}</p>
                      <p className="mt-1 text-sm text-slate-500">{formatDate(record.scheduledAt)}</p>
                      {payload.notes && <p className="mt-2 text-sm text-slate-600">{payload.notes}</p>}
                      {payload.feedback?.text && (
                        <div className="mt-3 rounded-2xl bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
                          Feedback: {payload.feedback.text}
                        </div>
                      )}
                      {payload.attendance?.markedAt && (
                        <div className="mt-3 rounded-2xl bg-sky-50 px-4 py-3 text-sm text-sky-900">
                          Attendance: student {payload.attendance.studentAttended ? 'present' : 'absent'}, tutor {payload.attendance.teacherAttended ? 'present' : 'absent'}
                          {payload.attendance.notes ? ` | ${payload.attendance.notes}` : ''}
                        </div>
                      )}
                    </div>
                  </div>

                  {canTeacherAct && (
                    <div className="mt-4 flex flex-wrap gap-2">
                      {record.status === 'REQUESTED' && (
                        <>
                          <button
                            type="button"
                            onClick={() => void updateDemo(record, 'ACCEPTED')}
                            className="rounded-2xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
                          >
                            Accept
                          </button>
                          <button
                            type="button"
                            onClick={() => void updateDemo(record, 'REJECTED')}
                            className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-2 text-sm font-semibold text-rose-800 hover:bg-rose-100"
                          >
                            Reject
                          </button>
                        </>
                      )}
                      {['ACCEPTED', 'RESCHEDULED', 'ATTENDANCE_MARKED'].includes(record.status) && (
                        <button
                          type="button"
                          onClick={() => void updateDemo(record, 'COMPLETED', { completedAt: new Date().toISOString() })}
                          className="rounded-2xl bg-slate-950 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
                        >
                          Mark Complete
                        </button>
                      )}
                    </div>
                  )}

                  {canTeacherAct && !['ATTENDANCE_MARKED', 'COMPLETED', 'FEEDBACK_SUBMITTED', 'CONVERTED', 'CANCELLED'].includes(record.status) && (
                    <div className="mt-3 grid gap-2 sm:grid-cols-[minmax(0,260px)_auto]">
                      <input
                        type="datetime-local"
                        value={rescheduleById[id] || ''}
                        onChange={(event) => setRescheduleById((prev) => ({ ...prev, [id]: event.target.value }))}
                        className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm"
                      />
                      <button
                        type="button"
                        onClick={() => void rescheduleDemo(record)}
                        className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100"
                      >
                        Reschedule
                      </button>
                    </div>
                  )}

                  {canTeacherAct && ['ACCEPTED', 'RESCHEDULED'].includes(record.status) && (
                    <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-3">
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Attendance</p>
                      <div className="mt-3 grid gap-2 md:grid-cols-[150px_150px_minmax(0,1fr)_auto]">
                        <select
                          value={(attendanceById[id]?.studentAttended || 'YES')}
                          onChange={(event) =>
                            setAttendanceById((prev) => ({
                              ...prev,
                              [id]: {
                                ...(prev[id] || { studentAttended: 'YES', teacherAttended: 'YES', notes: '' }),
                                studentAttended: event.target.value,
                              },
                            }))
                          }
                          className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm"
                        >
                          <option value="YES">Student present</option>
                          <option value="NO">Student absent</option>
                        </select>
                        <select
                          value={(attendanceById[id]?.teacherAttended || 'YES')}
                          onChange={(event) =>
                            setAttendanceById((prev) => ({
                              ...prev,
                              [id]: {
                                ...(prev[id] || { studentAttended: 'YES', teacherAttended: 'YES', notes: '' }),
                                teacherAttended: event.target.value,
                              },
                            }))
                          }
                          className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm"
                        >
                          <option value="YES">Tutor present</option>
                          <option value="NO">Tutor absent</option>
                        </select>
                        <input
                          value={attendanceById[id]?.notes || ''}
                          onChange={(event) =>
                            setAttendanceById((prev) => ({
                              ...prev,
                              [id]: {
                                ...(prev[id] || { studentAttended: 'YES', teacherAttended: 'YES', notes: '' }),
                                notes: event.target.value,
                              },
                            }))
                          }
                          className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm"
                          placeholder="Attendance note"
                        />
                        <button
                          type="button"
                          onClick={() => void recordAttendance(record)}
                          className="rounded-2xl bg-slate-950 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
                        >
                          Save
                        </button>
                      </div>
                    </div>
                  )}

                  {canStudentAct && record.status === 'REQUESTED' && (
                    <button
                      type="button"
                      onClick={() => void updateDemo(record, 'CANCELLED')}
                      className="mt-4 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100"
                    >
                      Cancel Request
                    </button>
                  )}

                  {canStudentAct && record.status === 'COMPLETED' && (
                    <div className="mt-4">
                      <label className="mb-1 block text-sm font-medium text-slate-700">Demo feedback</label>
                      <textarea
                        value={feedbackById[id] || ''}
                        onChange={(event) => setFeedbackById((prev) => ({ ...prev, [id]: event.target.value }))}
                        rows={3}
                        className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm"
                        placeholder="Share demo feedback..."
                      />
                      <div className="mt-2 flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => void submitFeedback(record)}
                          className="rounded-2xl bg-slate-950 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
                        >
                          Submit Feedback
                        </button>
                        <button
                          type="button"
                          onClick={() => void convertDemo(record)}
                          className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-800 hover:bg-emerald-100"
                        >
                          Continue With Tutor
                        </button>
                      </div>
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default DemoClassPanel;
